import { useState } from "react";
import { Plus, Pencil } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { http, errMsg } from "@/lib/api";
import { ICONS, ServiceIcon } from "@/components/site/ServiceIcon";
import { F, inputCls, StatusPill, ConfirmDelete, MoveButtons, moveItem, linesToList, PublishControls, FormButtons, saveItem } from "@/components/admin/shared";

const blank = { name: "", identity: "", icon: "sparkles", short: "", description: "", highlights: "", info: "" };

const CategoryForm = ({ initial, onSaved, onClose }) => {
  const [f, setF] = useState(initial);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const save = async (e, publish = false) => {
    e?.preventDefault();
    setBusy(true);
    const body = { ...f, highlights: linesToList(f.highlights), info: linesToList(f.info) };
    try {
      await saveItem("categories", f, body, publish);
      toast.success(publish ? "Layanan disimpan & diterbitkan" : "Draft layanan disimpan");
      onSaved();
      onClose();
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  return (
    <form onSubmit={save} className="grid sm:grid-cols-2 gap-3" data-testid="category-form">
      <F label="Nama layanan *"><input required className={inputCls} value={f.name} onChange={set("name")} data-testid="category-name-input" /></F>
      <F label="Identitas layanan" hint="Contoh: PMB Nina (khusus Kebidanan)"><input className={inputCls} value={f.identity} onChange={set("identity")} data-testid="category-identity-input" /></F>
      <F label="Ikon"><select className={inputCls} value={f.icon} onChange={set("icon")} data-testid="category-icon-select">{Object.keys(ICONS).map((k) => <option key={k} value={k}>{k}</option>)}</select></F>
      <F label="Deskripsi singkat" className="sm:col-span-2"><textarea rows={3} className={inputCls} value={f.description} onChange={set("description")} data-testid="category-description-input" /></F>
      <F label="Highlight layanan" hint="Satu highlight per baris" className="sm:col-span-2"><textarea rows={3} className={inputCls} value={f.highlights} onChange={set("highlights")} data-testid="category-highlights-input" /></F>
      <F label="Informasi penting" hint="Satu informasi per baris" className="sm:col-span-2"><textarea rows={2} className={inputCls} value={f.info} onChange={set("info")} data-testid="category-info-input" /></F>
      <FormButtons busy={busy} onPublish={() => save(null, true)} testId="category" />
    </form>
  );
};

export const CategoriesPanel = ({ categories, reload, mode = "list" }) => {
  const detail = mode === "detail";
  const [editing, setEditing] = useState(null);
  const remove = async (c) => { await http.delete(`/admin/categories/${c.id}`); toast.success("Layanan dihapus"); reload(); };
  const move = async (i, d) => { if (await moveItem(categories, i, d, "/admin/categories/reorder")) reload(); };
  const open = (c) => setEditing(c ? { ...c, highlights: (c.highlights || []).join("\n"), info: (c.info || []).join("\n") } : { ...blank });

  return (
    <div data-testid={detail ? "details-panel" : "categories-panel"}>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
        <p className="text-sm text-nanina-muted">{detail ? "Kelola deskripsi, highlight, dan informasi penting setiap layanan." : "Kelola kategori layanan dan urutan tampil."}</p>
        {!detail && <button onClick={() => open(null)} className="self-start inline-flex items-center gap-1.5 rounded-full bg-nanina-green text-white text-sm font-semibold px-4 py-2" data-testid="category-add-button"><Plus className="h-4 w-4" /> Tambah</button>}
      </div>
      <ul className="space-y-2">
        {categories.map((c, i) => (
          <li key={c.id} className="flex flex-wrap items-center gap-3 rounded-2xl bg-white border border-nanina-line p-3" data-testid={`admin-category-${c.slug}`}>
            <span className="grid place-items-center h-10 w-10 shrink-0 rounded-xl bg-nanina-skysoft text-nanina-green"><ServiceIcon name={c.icon} /></span>
            <div className="min-w-0 flex-1">
              <p className="font-semibold text-sm text-nanina-green truncate">{c.name} {c.identity && <span className="font-normal text-nanina-muted">· {c.identity}</span>}</p>
              <StatusPill item={c} />
              {detail && <p className="mt-1 text-xs text-nanina-muted line-clamp-2" data-testid={`detail-highlights-${c.slug}`}>{(c.highlights || []).join(" · ") || "Belum ada highlight"}</p>}
            </div>
            <PublishControls item={c} coll="categories" reload={reload} testId={`category-${c.slug}`} />
            {!detail && <MoveButtons onUp={() => move(i, -1)} onDown={() => move(i, 1)} testId={`category-move-${c.slug}`} />}
            <button onClick={() => open(c)} className="p-2 rounded-lg hover:bg-nanina-bg" aria-label="Edit" data-testid={`category-edit-${c.slug}`}><Pencil className="h-4 w-4" /></button>
            {!detail && <ConfirmDelete label={c.name} onConfirm={() => remove(c)} testId={`category-delete-${c.slug}`} />}
          </li>
        ))}
      </ul>
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing?.id ? "Edit layanan" : "Tambah layanan"}</DialogTitle><DialogDescription>Perubahan tampil di website setelah status Terbit.</DialogDescription></DialogHeader>
          {editing && <CategoryForm initial={editing} onSaved={reload} onClose={() => setEditing(null)} />}
        </DialogContent>
      </Dialog>
    </div>
  );
};
